import { Download, Maximize2, Minimize2 } from 'lucide-react'
import * as React from 'react'

import { cn } from '@/utils'
import { Button } from '../clickable/Button'
import {
  type DateRangeType,
  exportChartAsPNG,
  type Filter,
  type SchemaField,
} from './utils/exportChart'

interface ChartControlsProps extends React.HTMLAttributes<HTMLDivElement> {
  containerRef: React.RefObject<HTMLDivElement | null>
  isFullscreen: boolean
  toggleFullscreen: () => Promise<void>
  fullscreenError?: string | null
  fullscreenSupported?: boolean
  filename?: string
  projectName?: string
  title?: string
  filters?: Filter[]
  schema?: SchemaField[]
  dateRange?: DateRangeType
  dataDateRange?: { from: Date | string; to: Date | string }
  size?: 'sm' | 'md'
  floating?: boolean
}

export const ChartControls = React.forwardRef<
  HTMLDivElement,
  ChartControlsProps
>(
  (
    {
      containerRef,
      isFullscreen,
      toggleFullscreen,
      fullscreenError,
      fullscreenSupported = true,
      filename = 'chart-export.png',
      projectName,
      title,
      filters,
      schema,
      dateRange,
      dataDateRange,
      size = 'sm',
      floating = true,
      className,
      ...props
    },
    ref,
  ) => {
    const handleDownload = React.useCallback(() => {
      exportChartAsPNG(containerRef, filename, {
        projectName,
        title,
        filters,
        schema,
        dateRange,
        dataDateRange,
      })
    }, [
      containerRef,
      filename,
      projectName,
      title,
      filters,
      schema,
      dateRange,
      dataDateRange,
    ])

    const iconSize = size === 'sm' ? 'h-3.5 w-3.5' : 'h-4 w-4'
    const buttonSize = size === 'sm' ? 'xs' : 'sm'

    const fullscreenLabel = fullscreenError
      ? fullscreenError
      : !fullscreenSupported
        ? 'Fullscreen not supported'
        : isFullscreen
          ? 'Exit Fullscreen'
          : 'Enter Fullscreen'

    return (
      <div
        ref={ref}
        className={cn(
          'flex gap-2',
          floating &&
            'absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity z-10',
          !floating && 'gap-1',
          className,
        )}
        {...props}
      >
        <Button
          theme="outline"
          size={buttonSize}
          isIcon
          className={cn(
            floating
              ? 'bg-background/80 backdrop-blur-sm cursor-pointer hover:bg-background'
              : 'p-0',
          )}
          onClick={handleDownload}
          aria-label="Export as PNG"
        >
          <Download className={iconSize} />
        </Button>
        <Button
          theme="outline"
          size={buttonSize}
          isIcon
          className={cn(
            floating
              ? 'bg-background/80 backdrop-blur-sm cursor-pointer hover:bg-background'
              : 'p-0',
          )}
          onClick={toggleFullscreen}
          disabled={!fullscreenSupported}
          aria-label={fullscreenLabel}
          title={fullscreenLabel}
        >
          {isFullscreen ? (
            <Minimize2 className={iconSize} />
          ) : (
            <Maximize2 className={iconSize} />
          )}
        </Button>
      </div>
    )
  },
)
ChartControls.displayName = 'ChartControls'
